import { useState, useEffect, useCallback } from 'react';

export const useSessionTimer = () => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [sessionProgress, setSessionProgress] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [sessionComplete, setSessionComplete] = useState<boolean>(false);
  const [isWordGoalMode, setIsWordGoalMode] = useState<boolean>(false);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [sessionStartTime, setSessionStartTime] = useState<number>(0);

  // Start session timer
  const startSession = useCallback((duration: number, wordGoalMode: boolean = false) => {
    // Convert minutes to seconds
    const durationInSeconds = duration * 60;
    const now = Date.now();
    setTotalDuration(durationInSeconds);
    setTimeRemaining(durationInSeconds);
    setSessionProgress(0);
    setElapsedTime(0);
    setSessionStartTime(now);
    setIsRunning(true);
    setSessionComplete(false);
    setIsWordGoalMode(wordGoalMode);
  }, []);

  // Stop session timer
  const stopSession = useCallback(() => {
    setIsRunning(false);
    setTimeRemaining(0);
    setSessionProgress(0);
    setSessionComplete(false);
    setElapsedTime(0);
    setSessionStartTime(0);
  }, []);

  // Update timer every second when running
  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - sessionStartTime) / 1000);
      setElapsedTime(elapsed);

      if (!isWordGoalMode) {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Session complete
            clearInterval(timer);
            setIsRunning(false);
            setSessionComplete(true);
            return 0;
          }
          return prev - 1;
        });

        // Update progress percentage
        if (totalDuration > 0) {
          const progress = (elapsed / totalDuration) * 100;
          setSessionProgress(Math.min(100, progress));
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning, isWordGoalMode, totalDuration, sessionStartTime]);

  return {
    timeRemaining,
    sessionProgress,
    startSession,
    stopSession,
    isRunning,
    isWordGoalMode,
    sessionComplete,
    elapsedTime
  };
};

export default useSessionTimer; 